import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import MerchantModeSelector from './MerchantModeSelector';
import AllowedDomainsManager from './AllowedDomainsManager';
import MerchantPayoutDashboard from './MerchantPayoutDashboard';
import MerchantPayoutApprovals from './MerchantPayoutApprovals';
import usePagination from '../hooks/usePagination';
import Pagination from './Pagination';

const Dashboard = () => {
  const { currentUser } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [txStatusFilter, setTxStatusFilter] = useState('all');
  const [txPaymentMethodFilter, setTxPaymentMethodFilter] = useState('all');
  const [txSearchId, setTxSearchId] = useState('');
  const [txMinAmount, setTxMinAmount] = useState('');
  const [txMaxAmount, setTxMaxAmount] = useState('');
  const txPagination = usePagination(transactions, 10);
  const [stats, setStats] = useState({
    totalTransactions: 0,
    successfulTransactions: 0,
    totalAmount: 0,
    successRate: 0
  });
  const [apiKey, setApiKey] = useState('');
  const [merchantId, setMerchantId] = useState('');
  const [dateRange, setDateRange] = useState('today');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    const hash = window.location.hash.replace('#', '');
    return ['overview', 'payouts', 'approvals', 'configuration', 'kyc'].includes(hash) ? hash : 'overview';
  });

  const switchTab = (tab) => {
    window.location.hash = tab;
    setActiveTab(tab);
  };

  // Redirect admin users to admin dashboard
  useEffect(() => {
    if (currentUser?.role === 'ADMIN') {
      window.location.href = '/admin';
      return;
    }
  }, [currentUser]);

  // Reset to Overview tab when navbar Dashboard link is clicked while already on this page
  useEffect(() => {
    const handler = () => setActiveTab('overview');
    window.addEventListener('resetDashboardTab', handler);
    return () => window.removeEventListener('resetDashboardTab', handler);
  }, []);

  // ── Timezone handling ────────────────────────────────────────────────────
  // The DB stores timestamps in UTC. Users think in IST and we display IST.
  // So all date-range filters are computed as IST calendar boundaries, then
  // converted to the equivalent UTC instant before being sent to the backend
  // (which compares against UTC created_at). Display then adds +5:30 back.
  // This is independent of the viewer's browser timezone.
  //
  // Flow: user picks Sep 20 → IST 00:00–23:59 → (−5:30) → UTC range → DB query
  //       → UTC rows → (+5:30) → IST display.
  const IST_OFFSET_MS = (5 * 60 + 30) * 60 * 1000;

  // "Now" expressed as IST wall-clock, independent of the browser timezone.
  const istNow = () => new Date(Date.now() + IST_OFFSET_MS);

  // Given IST calendar parts, return the equivalent UTC Date (the real instant).
  // Build the IST wall-clock as a UTC-based epoch, then subtract the offset.
  const istPartsToUtc = (y, mZeroBased, d, hh = 0, mi = 0, ss = 0) => {
    const istAsUtcMs = Date.UTC(y, mZeroBased, d, hh, mi, ss);
    return new Date(istAsUtcMs - IST_OFFSET_MS);
  };

  const getDateRangeFilter = (range) => {
    const n = istNow(); // read IST wall-clock parts via getUTC* accessors
    const y = n.getUTCFullYear();
    const m = n.getUTCMonth();
    const d = n.getUTCDate();

    switch(range) {
      case 'today':
        // IST 00:00 today → UTC
        return istPartsToUtc(y, m, d, 0, 0, 0);

      case 'week':
        // Last 7 days (rolling, including today): IST 00:00 six days ago → UTC
        return istPartsToUtc(y, m, d - 6, 0, 0, 0);

      case 'month':
        // IST 00:00 on the 1st of this month → UTC
        return istPartsToUtc(y, m, 1, 0, 0, 0);

      case 'year':
        // IST 00:00 on Jan 1 → UTC
        return istPartsToUtc(y, 0, 1, 0, 0, 0);

      case 'custom': {
        // customFrom is "YYYY-MM-DD" — treat as IST 00:00 of that day → UTC
        if (!customFrom) return null;
        const [cy, cm, cd] = customFrom.split('-').map(Number);
        return istPartsToUtc(cy, cm - 1, cd, 0, 0, 0);
      }

      case 'lifetime':
      default:
        return null;
    }
  };

  const getDateRangeEnd = (range) => {
    if (range === 'custom' && customTo) {
      // IST end-of-day (23:59:59) of the selected day → UTC
      const [cy, cm, cd] = customTo.split('-').map(Number);
      return istPartsToUtc(cy, cm - 1, cd, 23, 59, 59);
    }
    return null; // no end cap for preset ranges
  };

  const filterTransactionsByDate = (transactions, range) => {
    const filterDate = getDateRangeFilter(range);
    const filterEnd  = getDateRangeEnd(range);
    
    if (!filterDate) return transactions;
    
    return transactions.filter(transaction => {
      const d = new Date(transaction.createdAt);
      if (filterEnd) return d >= filterDate && d <= filterEnd;
      return d >= filterDate;
    });
  };

  const fetchTransactions = useCallback(async () => {
    try {
      // Only fetch for merchants
      if (currentUser?.role !== 'MERCHANT') {
        setLoading(false);
        return;
      }
      
      // Build server-side filter params (without size — resolved below).
      const buildParams = (size) => {
        const params = new URLSearchParams();
        params.set('page', '0');
        params.set('size', String(size));

        // Date range
        const filterDate = getDateRangeFilter(dateRange);
        const filterEnd = getDateRangeEnd(dateRange);
        if (filterDate) params.set('fromDate', filterDate.toISOString().slice(0, 19));
        if (filterEnd) params.set('toDate', filterEnd.toISOString().slice(0, 19));

        // Status filter
        if (txStatusFilter && txStatusFilter !== 'all') params.set('status', txStatusFilter);
        // Payment method filter
        if (txPaymentMethodFilter && txPaymentMethodFilter !== 'all') params.set('paymentMethod', txPaymentMethodFilter);
        // Transaction ID search
        if (txSearchId && txSearchId.trim()) params.set('transactionId', txSearchId.trim());
        // Amount filters
        if (txMinAmount) params.set('minAmount', txMinAmount);
        if (txMaxAmount) params.set('maxAmount', txMaxAmount);
        return params;
      };

      // First fetch with a large page. If the total exceeds what we pulled,
      // refetch once with the exact total so NO transactions are ever capped.
      const INITIAL_SIZE = 100000;
      let response = await axios.get(`/api/payment/merchant/payments?${buildParams(INITIAL_SIZE).toString()}`);

      if (response.data.success) {
        let data = response.data.data;
        const total = typeof data.totalElements === 'number' ? data.totalElements : (data.content || []).length;
        if (total > (data.content || []).length) {
          // Pull everything in a single page sized to the full result set
          response = await axios.get(`/api/payment/merchant/payments?${buildParams(total).toString()}`);
          if (response.data.success) data = response.data.data;
        }
        setTransactions(data.content || []);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error.message);
      
      // Handle 401 errors by redirecting to login
      if (error.response?.status === 401) {
        console.error('DEBUG: 401 error, redirecting to login');
        window.location.href = '/merchant/login';
      }
    }
  }, [currentUser, dateRange, customFrom, customTo, txStatusFilter, txPaymentMethodFilter, txSearchId, txMinAmount, txMaxAmount]);

  useEffect(() => {
    if (currentUser?.role === 'MERCHANT') {
      fetchTransactions();
      fetchStats();
      fetchApiKey();

      // Auto-refresh every 30 seconds
      const interval = setInterval(() => {
        fetchTransactions();
        fetchStats();
      }, 30000);
      return () => clearInterval(interval);
    } else {
      setLoading(false);
    }
  }, [dateRange, customFrom, customTo, txStatusFilter, txPaymentMethodFilter, txSearchId, txMinAmount, txMaxAmount, fetchTransactions, currentUser]);

  const fetchApiKey = async () => {
    try {
      console.log('=== DEBUG: fetchApiKey called ===');
      
      console.log('DEBUG: Token found, calling /api/merchant/config/my-api-key');
      const response = await axios.get('/api/merchant/config/my-api-key');
      console.log('DEBUG: API key response:', response.data);
      
      if (response.data.success) {
        setApiKey(response.data.data.apiKey);
        setMerchantId(response.data.data.merchantId);
        console.log('DEBUG: API key and merchant ID set successfully');
      }
    } catch (error) {
      console.error('=== ERROR: fetchApiKey failed ===');
      console.error('Error object:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      
      // Handle 401 errors by redirecting to login
      if (error.response?.status === 401) {
        console.error('DEBUG: 401 error - redirecting to login');
        window.location.href = '/merchant/login';
      } else if (error.response?.status === 404) {
        console.error('DEBUG: 404 error - merchant record not found');
        console.error('You may need to create a merchant record for this user');
      } else if (!error.response) {
        console.error('DEBUG: Network error - merchant service may not be running');
        console.error('Check if merchant service is running on port 8082');
      }
    }
  };



  const fetchStats = async () => {
    try {
      // Only fetch for merchants
      if (currentUser?.role !== 'MERCHANT') {
        setLoading(false);
        return;
      }
      
      // Fetch ALL rows in the selected range to compute accurate stats.
      // Adaptive: learn the total from the first response, then refetch the
      // full set if needed so nothing is capped.
      const buildStatsParams = (size) => {
        const params = new URLSearchParams();
        params.set('page', '0');
        params.set('size', String(size));
        const filterDate = getDateRangeFilter(dateRange);
        const filterEnd = getDateRangeEnd(dateRange);
        if (filterDate) params.set('fromDate', filterDate.toISOString().slice(0, 19));
        if (filterEnd) params.set('toDate', filterEnd.toISOString().slice(0, 19));
        return params;
      };

      const INITIAL_SIZE = 100000;
      let response = await axios.get(`/api/payment/merchant/payments?${buildStatsParams(INITIAL_SIZE).toString()}`);
      if (response.data.success) {
        let data = response.data.data;
        const totalCount = typeof data.totalElements === 'number' ? data.totalElements : (data.content || []).length;
        if (totalCount > (data.content || []).length) {
          response = await axios.get(`/api/payment/merchant/payments?${buildStatsParams(totalCount).toString()}`);
          if (response.data.success) data = response.data.data;
        }
        const payments = data.content || [];

        // Authoritative total from server (not limited by fetched page length)
        const totalTransactions = typeof data.totalElements === 'number'
          ? data.totalElements
          : payments.length;
        const successfulTransactions = payments.filter(p => p.status === 'SUCCESS').length;
        // Total collected = sum of the ACTUAL amount paid (bank response) for
        // successful payments only. No fallback to the intent amount — the total
        // must reflect money actually received, not what was requested.
        const totalAmount = payments
          .filter(p => p.status === 'SUCCESS')
          .reduce((sum, p) => {
            const paid = (p.paidAmount !== null && p.paidAmount !== undefined && p.paidAmount !== '')
              ? parseFloat(p.paidAmount)
              : 0;
            return sum + paid;
          }, 0);
        const successRate = totalTransactions > 0 ? (successfulTransactions / totalTransactions * 100) : 0;

        setStats({
          totalTransactions,
          successfulTransactions,
          totalAmount,
          successRate
        });
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      
      // Handle 401 errors by redirecting to login
      if (error.response?.status === 401) {
        window.location.href = '/merchant/login';
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusClass = status === 'SUCCESS' ? 'status-success' : 
                       status === 'PENDING' ? 'status-pending' : 'status-failed';
    return <span className={`status-badge ${statusClass}`}>{status}</span>;
  };

  // createdAt is a naive UTC timestamp from the backend (e.g. "2026-09-15T22:03:37").
  // The DB stores UTC; the dashboard displays IST. So parse as UTC and add the
  // +5:30 offset before formatting. Date.UTC + getUTC* accessors handle the
  // day/month/year rollover when the offset crosses midnight.
  const formatTxnDateTime = (value) => {
    if (!value) return '';
    const m = String(value).match(/(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})/);
    if (!m) return String(value);
    const [, yyyy, mm, dd, HH, MI, SS] = m;
    const utcMs = Date.UTC(+yyyy, +mm - 1, +dd, +HH, +MI, +SS);
    const ist = new Date(utcMs + (5 * 60 + 30) * 60 * 1000);
    const p = (n) => String(n).padStart(2, '0');
    let h = ist.getUTCHours();
    const ampm = h >= 12 ? 'pm' : 'am';
    h = h % 12 || 12;
    return `${p(ist.getUTCDate())}/${p(ist.getUTCMonth() + 1)}/${ist.getUTCFullYear()}, ${p(h)}:${p(ist.getUTCMinutes())} ${ampm}`;
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };



  // Export the currently filtered transactions to a CSV file. Uses the full
  // `transactions` list (all filtered rows), not just the current page.
  const downloadTransactionsCsv = () => {
    if (!transactions || transactions.length === 0) return;

    const headers = ['Transaction ID', 'UTR', 'Paid Amount', 'Request Amount', 'Status', 'Payment Method', 'Date'];

    const escapeCsv = (val) => {
      const s = val === null || val === undefined ? '' : String(val);
      // Wrap in quotes and escape embedded quotes to keep CSV valid.
      return `"${s.replace(/"/g, '""')}"`;
    };

    const rows = transactions.map((t) => {
      const method = t.upiProvider && t.upiProvider.startsWith('EASEBUZZ_')
        ? t.upiProvider.replace('EASEBUZZ_', '')
        : t.paymentMethod;
      const hasPaid = t.paidAmount !== null && t.paidAmount !== undefined && t.paidAmount !== '';
      const hasAmount = t.amount !== null && t.amount !== undefined && t.amount !== '';
      return [
        t.transactionId,
        t.utr || '',
        hasPaid ? t.paidAmount : '',
        hasAmount ? t.amount : '',
        t.status,
        method,
        formatTxnDateTime(t.createdAt)
      ].map(escapeCsv).join(',');
    });

    const csv = [headers.map(escapeCsv).join(','), ...rows].join('\r\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.download = `transactions_${stamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="container" role="status" aria-live="polite">Loading...</div>;
  }

  // Show message for non-merchant users
  if (currentUser?.role !== 'MERCHANT') {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
          <h2>Access Restricted</h2>
          <p>This dashboard is only available for merchant accounts.</p>
          <p>Current role: <strong>{currentUser?.role || 'Unknown'}</strong></p>
          {currentUser?.role === 'ADMIN' && (
            <div style={{ marginTop: '20px' }}>
              <button 
                className="btn btn-primary"
                onClick={() => window.location.href = '/admin'}
              >
                Go to Admin Dashboard
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <h1>Merchant Dashboard</h1>
      <p>Welcome back, {currentUser.username}!</p>

      {/* Navigation Tabs */}
      <div style={{ 
        display: 'flex', 
        gap: '10px', 
        marginBottom: '30px',
        borderBottom: '1px solid #ddd',
        paddingBottom: '10px'
      }}>
        <button 
          className={`btn ${activeTab === 'overview' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => switchTab('overview')}
        >
          📊 Overview
        </button>
        <button 
          className={`btn ${activeTab === 'payouts' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => switchTab('payouts')}
        >
          💸 Payouts
        </button>
        <button 
          className={`btn ${activeTab === 'approvals' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => switchTab('approvals')}
        >
          ⏳ Approvals
        </button>
        <button 
          className={`btn ${activeTab === 'configuration' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => switchTab('configuration')}
        >
          ⚙️ Configuration
        </button>
        <button 
          className={`btn ${activeTab === 'kyc' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => switchTab('kyc')}
        >
          📋 KYC Verification
        </button>
      </div>

      {activeTab === 'configuration' ? (
        <>
          {/* API Credentials moved here from Overview */}
          <div className="card">
            
            <p style={{ fontSize: '14px', color: '#666' }}>
              Use these credentials to integrate the payment gateway into your application.
            </p>
            <h3 style={{ marginTop: '20px' }}>Merchant App Setup:</h3>
            {/* <p style={{ fontSize: '14px' }}>Update your <code>test-merchant-app/.env</code> file:</p> */}
            <pre style={{ backgroundColor: '#f8f9fa', padding: '10px', borderRadius: '5px', fontSize: '12px', overflow: 'auto' }}>
{`REACT_APP_MERCHANT_ID=${merchantId}
REACT_APP_MERCHANT_API_KEY=${apiKey}`}
            </pre>
            <p style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
              Then restart the  app to create payments for your merchant.
            </p>
            {/* <h3 style={{ marginTop: '20px' }}>API Integration:</h3>
            <p><strong>Endpoint:</strong> <code>POST /api/payment/create</code></p>
            <p><strong>Payment Page URL:</strong> <code>/payment/[transactionId]</code></p>
            <h3>Sample Integration Code:</h3>
            <pre style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '5px', overflow: 'auto' }}>
{`// Create Payment
const response = await fetch('/api/payment/create', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer YOUR_API_KEY'
  },
  body: JSON.stringify({
    amount: 100.00,
    paymentMethod: 'UPI_QR',
    description: 'Product Purchase',
    callbackUrl: 'https://yoursite.com/callback'
  })
});

const payment = await response.json();
// Redirect to: /payment/\${payment.data.transactionId}`}
            </pre> */}
          </div>
          <AllowedDomainsManager />
        </>
      ) : activeTab === 'payouts' ? (
        <MerchantPayoutDashboard />
      ) : activeTab === 'approvals' ? (
        <MerchantPayoutApprovals />
      ) : activeTab === 'kyc' ? (
        <div className="kyc-integration-section">
          <div className="card">
        <h2>🔐 KYC Verification Status</h2>
            <p>Complete your Know Your Customer (KYC) verification to unlock full payment capabilities.</p>
            
            <div style={{ display: 'flex', gap: '15px', marginTop: '20px', flexWrap: 'wrap' }}>
              <button 
                className="btn btn-primary"
                onClick={() => window.location.href = '/merchant/kyc'}
                style={{ padding: '12px 24px', fontSize: '16px' }}
              >
                🚀 Go to KYC Dashboard
              </button>
              
              <button 
                className="btn btn-secondary"
                onClick={() => window.location.href = '/kyc/help'}
                style={{ padding: '12px 24px', fontSize: '16px' }}
              >
                ❓ KYC Help & Support
              </button>
              
              <button 
                className="btn btn-outline"
                onClick={() => window.location.href = '/kyc/demo'}
                style={{ 
                  padding: '12px 24px', 
                  fontSize: '16px',
                  background: 'transparent',
                  border: '2px solid #007a75',
                  color: '#007a75'
                }}
              >
                📖 View KYC Demo
              </button>
            </div>

            <div style={{ marginTop: '30px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <h3>📋 KYC Benefits</h3>
              <ul style={{ marginBottom: '0' }}>
                <li>✅ Accept higher transaction amounts</li>
                <li>✅ Reduced transaction fees</li>
                <li>✅ Enhanced security and trust</li>
                <li>✅ Compliance with regulatory requirements</li>
                <li>✅ Access to advanced merchant features</li>
              </ul>
            </div>
          </div>
        </div>
      ) : (
        <div>

      {/* Filters */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label htmlFor="dateRange" style={{ fontSize: '13px', fontWeight: '500', margin: 0 }}>Date Range:</label>
            <select
              id="dateRange"
              className="form-control"
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              style={{ width: '160px' }}
            >
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
              <option value="lifetime">Lifetime</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {dateRange === 'custom' && (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '13px', fontWeight: '500', margin: 0 }}>From:</label>
                <input
                  type="date"
                  className="form-control"
                  value={customFrom}
                  max={customTo || undefined}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  style={{ width: '160px' }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '13px', fontWeight: '500', margin: 0 }}>To:</label>
                <input
                  type="date"
                  className="form-control"
                  value={customTo}
                  min={customFrom || undefined}
                  onChange={(e) => setCustomTo(e.target.value)}
                  style={{ width: '160px' }}
                />
              </div>
              <button
                className="btn btn-primary"
                onClick={() => { if (customFrom && customTo) setDateRange('custom'); }}
                disabled={!customFrom || !customTo}
              >
                Apply
              </button>
            </>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label htmlFor="statusFilter" style={{ fontSize: '13px', fontWeight: '500', margin: 0 }}>Status:</label>
            <select
              id="statusFilter"
              className="form-control"
              value={txStatusFilter}
              onChange={(e) => setTxStatusFilter(e.target.value)}
              style={{ width: '130px' }}
            >
              <option value="all">All</option>
              <option value="SUCCESS">Success</option>
              <option value="PENDING">Pending</option>
              <option value="FAILED">Failed</option>
            </select>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label htmlFor="paymentMethodFilter" style={{ fontSize: '13px', fontWeight: '500', margin: 0 }}>Payment Method:</label>
            <select
              id="paymentMethodFilter"
              className="form-control"
              value={txPaymentMethodFilter}
              onChange={(e) => setTxPaymentMethodFilter(e.target.value)}
              style={{ width: '150px' }}
            >
              <option value="all">All</option>
              <option value="UPI_QR">UPI QR</option>
              <option value="UPI_INTENT">UPI Intent</option>
              <option value="UPI_COLLECT">UPI Collect</option>
              <option value="CARD">Card</option>
              <option value="NET_BANKING">Net Banking</option>
            </select>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label htmlFor="txSearchId" style={{ fontSize: '13px', fontWeight: '500', margin: 0 }}>Transaction ID:</label>
            <input
              id="txSearchId"
              type="text"
              className="form-control"
              value={txSearchId}
              onChange={(e) => setTxSearchId(e.target.value)}
              placeholder="Search by ID"
              style={{ width: '160px' }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label htmlFor="txMinAmount" style={{ fontSize: '13px', fontWeight: '500', margin: 0 }}>Min Amount:</label>
            <input
              id="txMinAmount"
              type="number"
              className="form-control"
              value={txMinAmount}
              onChange={(e) => { const val = e.target.value; if (val === '' || Number(val) >= 0) setTxMinAmount(val); }}
              onWheel={(e) => e.target.blur()} /* prevent mouse-wheel from changing the value */
              placeholder="0"
              min="0"
              style={{ width: '110px' }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label htmlFor="txMaxAmount" style={{ fontSize: '13px', fontWeight: '500', margin: 0 }}>Max Amount:</label>
            <input
              id="txMaxAmount"
              type="number"
              className="form-control"
              value={txMaxAmount}
              onChange={(e) => { const val = e.target.value; if (val === '' || Number(val) >= 0) setTxMaxAmount(val); }}
              onWheel={(e) => e.target.blur()} /* prevent mouse-wheel from changing the value */
              placeholder="No limit"
              min="0"
              style={{ width: '110px' }}
            />
          </div>

          {(dateRange !== 'today' || txStatusFilter !== 'all' || txPaymentMethodFilter !== 'all' || txSearchId || txMinAmount || txMaxAmount || customFrom || customTo) && (
            <button
              className="btn btn-secondary"
              onClick={() => { setDateRange('today'); setTxStatusFilter('all'); setTxPaymentMethodFilter('all'); setTxSearchId(''); setTxMinAmount(''); setTxMaxAmount(''); setCustomFrom(''); setCustomTo(''); }}
              style={{ fontSize: '12px', padding: '6px 12px' }}
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-3">
        <div className="card">
          <p className="stat-card-label">Total Transactions</p>
          <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#007a75' }}>
            {stats.totalTransactions}
          </p>
        </div>
        <div className="card">
          <p className="stat-card-label">Successful Transactions</p>
          <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#007a75' }}>
            {stats.successfulTransactions}
          </p>
        </div>
        <div className="card">
          <p className="stat-card-label">Total Amount</p>
          <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#034659' }}>
            {formatAmount(stats.totalAmount)}
          </p>
        </div>
      </div>

      {/* Success Rate */}
      <div className="card">
        <h2>Success Rate</h2>
        <div style={{ 
          width: '100%', 
          height: '20px', 
          backgroundColor: '#e9ecef', 
          borderRadius: '10px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${stats.successRate}%`,
            height: '100%',
            backgroundColor: '#28a745',
            transition: 'width 0.3s ease'
          }}></div>
        </div>
        <p style={{ marginTop: '10px', fontSize: '1.2rem', fontWeight: 'bold' }}>
          {stats.successRate.toFixed(1)}%
        </p>
      </div>

      {/* Transactions Table */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <h2 style={{ margin: 0 }}>Recent Transactions</h2>
          <button
            className="btn btn-primary"
            onClick={downloadTransactionsCsv}
            disabled={transactions.length === 0}
            style={{ fontSize: '13px', padding: '8px 16px' }}
            title="Download the filtered transactions as a CSV file"
          >
            ⬇️ Download CSV
          </button>
        </div>
        {transactions.length > 0 ? (
          <table className="table" role="table">
            <caption className="sr-only">Recent Transactions</caption>
            <thead>
              <tr>
                <th scope="col">Transaction ID</th>
                <th scope="col">UTR</th>
                <th scope="col">Paid Amount</th>
                <th scope="col">Request Amount</th>
                <th scope="col">Status</th>
                <th scope="col">Payment Method</th>
                <th scope="col">Date</th>
              </tr>
            </thead>
            <tbody>
              {txPagination.pageData.map((transaction) => {
                // Show ONLY the actual amount confirmed by the bank (paidAmount).
                // If the bank hasn't confirmed (pending/failed), show '-' — no money
                // was actually paid, so we must not display the requested amount here.
                const hasPaid = transaction.paidAmount !== null
                  && transaction.paidAmount !== undefined
                  && transaction.paidAmount !== '';
                return (
                <tr key={transaction.id}>
                  <td>{transaction.transactionId}</td>
                  <td>{transaction.utr || '-'}</td>
                  <td>{hasPaid ? formatAmount(transaction.paidAmount) : '-'}</td>
                  {/* Requested amount — always shown regardless of status (success/fail/pending) */}
                  <td>{(transaction.amount !== null && transaction.amount !== undefined && transaction.amount !== '')
                    ? formatAmount(transaction.amount) : '-'}</td>
                  <td>{getStatusBadge(transaction.status)}</td>
                  <td>
                    {transaction.upiProvider && transaction.upiProvider.startsWith('EASEBUZZ_')
                      ? transaction.upiProvider.replace('EASEBUZZ_', '') // show UPI / IMPS / NETBANKING
                      : transaction.paymentMethod}
                  </td>
                  <td>{formatTxnDateTime(transaction.createdAt)}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p>No transactions found for the selected period.</p>
        )}
        <Pagination {...txPagination} pageSize={10} />
      </div>

        </div>
      )}
    </div>
  );
};

export default Dashboard;
